<template>
  <div class="profile-editor">
    <div class="actions-row">
      <div class="select-container">
        <h5>Products</h5>

        <button class="button button-add-more"
          @click="openMultiProductModal()"
          title="Select multiple products">
          Select product(s)
        </button>
        <button class="button" @click="chooseDate()">Select Date</button>
        <div class="labs-loader" v-if="optionsLoading"></div>
        <div class="date-display">
          <strong class="text">Calculate as of</strong>
          <div class="date">
            <span v-if="asOf.type === 'static'">{{ asOfDate.toISOString().slice(0, 10) }}</span>
            <span v-else>{{ asOf.data.count }} {{ asOf.data.unit }}{{ asOf.data.count !== 1 ? 's' : '' }} ago</span>
          </div>
        </div>
      </div>
    </div>
    <div class="multi-products-row" v-if="selectedProducts.length >= 1">
      <div class="multi-products-list" :title="selectedProductsString">
        {{ selectedProducts.length }} products selected
      </div>
      <span class="pseudo-link" @click="openMultiProductModal()">
        [Edit]
      </span>
    </div>

    <div class="create-profile-row">
      <div class="select-column">
        <h5>
          Sources
        </h5>
        <div class="select-container">
          <div class="select-all-items" v-if="dataSources.length">
            <div>
              {{ selectedDataSources.length }} selected
            </div>
            <button class="button" @click="addAllDataSources()">
              <span v-if="selectedDataSources.length !== dataSources.length">Add</span><span v-else>Remove</span>&nbsp;All ({{ dataSources.length }})
            </button>
          </div>
          <div class="select-header" v-if="dataSources.length">
            <span>
              No. of Instruments
              <span class="info-icon" v-tooltip="{
                content: 'For selected product type(s), the number of instruments for which any trade has been observed over the last 2 years'
              }">
                <i class="material-icons info-outline">info_outline</i><i class="material-icons info">info</i>
              </span>
            </span>
          </div>
          <div class="select-scrollable">
            <div class="select-inner" :class="{'select-inner--empty': !dataSources.length}">
              <div class="select-all-empty" v-if="!dataSources.length">
                <span class="icon-layout-3"></span>
                <p>
                  Select a Product to begin building a Profile
                </p>
              </div>
              <div class="select-item select-item--with-count"
                :class="{'select-item--active': selectedDataSources.indexOf(ds.id) > -1}"
                v-for="(ds, index) of dataSources"
                :key="'ds_' + index"
                @click="toggleSelectedDataSourceItem(ds.id)"
              >
                <div class="select-item__left" :title="venuesDisplayMap[ds.id] || ds.id">
                  <div class="select-item__checkbox"></div>
                  {{ venuesDisplayMap[ds.id] || ds.id }}
                </div>
                <div class="select-item__count">
                  {{ ds.count }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="select-column">
        <h5 title="Attributes for Risk Factor Mapping">
          Attributes to Group By
        </h5>
        <div class="select-container">
          <div class="select-all-items" v-if="attributes.length">
            <div>
              {{ selectedAttributes.length }} selected
            </div>
            <button class="button" @click="addAllAttributes()">
              <span v-if="selectedAttributes.length !== attributes.length">Add</span><span v-else>Remove</span>&nbsp;All ({{ attributes.length }})
            </button>
          </div>
          <div class="select-header" v-if="attributes.length">
            <span>
              Distinct Values
              <span class="info-icon" v-tooltip="{
                content: 'For selected product type(s), source(s) and 1 year test period, the number of distinct values of this attribute'
              }">
                <i class="material-icons info-outline">info_outline</i><i class="material-icons info">info</i>
              </span>
            </span>
          </div>
          <div class="select-scrollable">
            <div class="select-inner" :class="{'select-inner--empty': !attributes.length}">
              <div class="select-all-empty" v-if="!attributes.length">
                <span class="icon-layout-3"></span>
                <p v-if="!selectedProducts.length">
                  Select a Product to begin building a Profile
                </p>
                <p v-else>
                  Select Data Sources to see available product attributes
                </p>
              </div>
              <div class="select-item select-item--with-count"
                :class="{'select-item--active': selectedAttributes.indexOf(attr.id) > -1}"
                v-for="(attr, index) of attributes" :key="'ds_' + index"
                @click="selectAttributesItem(attr.id)"
              >
                <div class="select-item__left" :title="attr.display">
                  <div class="select-item__checkbox"></div>
                  {{ attr.display }}
                  <div class="select-item__bucketing" @click.stop.prevent="selectBucketing(attr.id)"
                    v-if="attr.fixed_order"
                  >
                    <i class="material-icons">edit</i>
                    {{ returnSelectedBucketingString(attr.id) }}
                  </div>
                </div>
                <div class="select-item__count" v-if="attrValues && !attrValueQueryActive && !showDevAttrCount">
                  {{ attrValues[attr.id] }}
                </div>
                <div class="select-item__count select-item__count--dev" v-if="showDevAttrCount">
                  {{ attr.count }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Watch } from 'vue-property-decorator';
import FRTBApi from '@/api/frtb.api';
import _ from 'lodash';
import Env from '@/services/env.service';
import UtilsService from '../services/utils.service';
import GAService from '@/services/ga.service';
import { AsOf, ProfileForRequest, VenueWithEmbeddedProductInfo, Attribute, ProductWithChildren, Product, Bucket, BucketMap } from '../store/types';

@Component({
  components: {},
})
export default class CreateProfile extends Vue {

  public dataSources: VenueWithEmbeddedProductInfo[] = [];
  public attributes: any[] = [];
  public calculateLoading: boolean = false;
  public pauseCalculateTrigger: boolean = false;

  get showDevAttrCount() {
    return Env.getBool('VUE_APP_SHOW_ATTR_COUNT')
  }

/***********************************************
 * 
 *       ATTRIBUTE VALUES
 * 
 *       The values next to the attribute names.
 *       Retrieved from /distinct_attribute_values
 *         endpoint          
 * 
 ***********************************************/

    get attrValues() {
      return this.$store.state.attributeCounts.counts || {};
    }

    get attrValueQueryActive() {
      return this.$store.state.attributeCounts.queryActive || false;
    }

    public triggerAttrValuesRequest() {
      if (!this.asOfError && this.selectedDataSources.length && this.selectedProducts.length) {
        this.$store.dispatch('getAttrValues', {
          basicProfile: {
            asOf: this.asOf,
            sources: this.selectedDataSources,
            types: this.selectedProducts
          }
        });
      }
    }
  
/***********************************************
 * 
 *       OPTIONS
 * 
 *       The values to populate lists/ dropdowns,
 *       providing the options the users can 
 *       select.                
 * 
 ***********************************************/

  public get optionsData() {
    return this.$store.state.options.options || {};
  }

  public get venuesDisplayMap() {
    return this.$store.state.options.venuesDisplayMap || {};
  }

  public get attributesDisplayMap() {
    return this.$store.state.options.attributesDisplayMap || {};
  }

  public get productsById(): {[id: string] : Product} {
    return this.$store.state.options.productsById || {};
  }

  get optionsLoading() {
    return _.isEmpty(this.optionsData);
  }

  /****************************************************************************************
   *      DATA SOURCES
   *      The available data sources are dependent on which products are selected.
   *      This function looks at data-source info in our products, and creates an 
   *      array of unique data-sources (venues) for display. Each data-source item
   *      has embedded info about the products they were derived from.
   * 
   *      This function is called whenever our selectedProducts changes (see the 
   *      watcher onSelectedProductsChanged)
   ****************************************************************************************/

  public generateAvailableDataSources() {
    let sources: VenueWithEmbeddedProductInfo[] = [];
    let sourceIds: string[] = [];

    if (this.selectedProducts.length) {
      this.selectedProducts.forEach( (product: string) => {
        const venues = this.productsById[product].venues || [];
        venues.forEach( (venue: any) => {
          const index = sourceIds.indexOf(venue.id);
          if (index < 0) {
            sourceIds.push(venue.id);
            sources.push({...venue, productIds: [product]});
          } else {
            if (sources[index].productIds.indexOf(product) < 0) {
              sources[index].productIds.push(product);
              const newAttrCounts: number[] = sources[index].attribute_counts.map((a, i) => {
                return a + venue.attribute_counts[i]
              });

              sources[index].attribute_counts = newAttrCounts;
              sources[index].count = sources[index].count + venue.count;
            }
          }
        });
      });
    }

    this.dataSources = _.orderBy(sources, s => {
      return s.display ? s.display.toLowerCase() : s.id.toLowerCase();
    });
  }

  /****************************************************************************************
   *      ATTRIBUTES
   ****************************************************************************************/

  public generateAvailableAttributes() {
    if (!this.selectedDataSources.length) {
      this.$store.commit('setSelectedAttributes', []);
      this.attributes = [];
      return;
    }
    
    // Get an array of selected data source (venue) objects
    const fullVenueObjects = this.selectedDataSources.reduce((arr: VenueWithEmbeddedProductInfo[], venueId) => {
      const found:VenueWithEmbeddedProductInfo | undefined = this.dataSources.find((ds) => ds.id === venueId);
      if (found) {
        arr.push(found);
      }
      return arr;
    }, []);
    // Data Sources have a count
    const venuesCountArr = fullVenueObjects.map((fullVenue) => fullVenue.count);
    const reducer = (accumulator: number, currentValue: number) => accumulator + currentValue;
    const venuesCount = venuesCountArr.reduce(reducer);
    const minCount = Math.ceil(0.1 * venuesCount);

    const attributeCountsArrays = fullVenueObjects.map((fullVenue) => fullVenue.attribute_counts );
    const attributeCounts = attributeCountsArrays.reduce((r: number[], a) => a.map((b, i) => (r[i] || 0) + b), []);

    const attributes: Attribute[] = [];
    this.optionsData.attributes.forEach( (attr: Attribute, index: number) => {
      const count = attributeCounts[index] || 0;
      if (count >= minCount) {
        attributes.push({
          id: attr.id,
          display: this.attributesDisplayMap[attr.id] || attr.id,
          fixed_order: attr.fixed_order,
          bucketed: attr.bucketed,
          count
        });
      }
    });
    
    this.attributes = _.orderBy(attributes, 'display');

    this.updateSelectedAttributesAfterAttributeListHasBeenEdited();
  }

/***********************************************
 * 
 * 
 *              USER SELECTIONS
 * 
 * 
 ***********************************************/

  /**
   * Changes to user selectins can be triggered by loading a profile.
   * Typically, in such cases, we may want to suppress some actions that
   * selections would trigger (ie, changing a product selection would typically
   * reset selected data sources and attributes. As our profile should have 
   * its own selected data sources and attributes, we suppress the resets).
   * 
   * This flag indicates when we are loading from the profile.
   */
  get isLoadingFromProfile() {
    return this.$store.state.selections.isLoadingFromProfile || false;
  }

  @Watch('isLoadingFromProfile')
  private profileLoadingChange(value: boolean, oldValue: boolean) {
    if (!value && oldValue) {
      this.generateAvailableDataSources();
      this.generateAvailableAttributes();
      this.triggerAttrValuesRequest();
    }
  }

/****************************************************************************************
 *      PRODUCTS
 *      Products are selected by a user in the modal MultiProductModal (or when a profile 
 *      is loaded). We respond to changes with a watcher (onSelectedProductsChanged).
 ****************************************************************************************/

  public openMultiProductModal() {
    this.$store.commit('setModal', 'multiProduct');
  }

  get selectedProducts(): string[] {
    return this.$store.state.selections.selectedProducts || [];
  }

  /** 
   * Creates a string that is a list of selected products
   * Currently just used as a title (hover) attr on "n products selected" string
   */
  get selectedProductsString() {
    let str = '';
    let commaLimit = this.selectedProducts.length - 1;
    this.selectedProducts.forEach((id: string, index: number) => {
      str += this.productsById[id] ? this.productsById[id].display : id;
      if (index < commaLimit) {
        str += ', '
      }
    });

    return str;
  }

  @Watch('selectedProducts')
  private onSelectedProductsChanged(value: string[], oldValue: string[]) {
    if (!this.isLoadingFromProfile) {
      this.$store.commit('setSelectedAttributes', []);
      this.$store.commit('setSelectedDataSources', []);
      this.generateAvailableDataSources();
      this.attributes = [];
    }
  }

/****************************************************************************************
 *      DATES
 *      TODO: describe   
 ****************************************************************************************/

  public chooseDate(): void {
    this.$store.commit('setModal', 'dateSelect');
  }

  public get asOf(): AsOf {
    return this.$store.getters.getAsOf;
  }

  public get asOfDate(): Date {
    return this.$store.getters.getAsOfDate;
  }

  @Watch('asOf')
  private onAsOfChange() {
    if (!this.isLoadingFromProfile) {
      this.triggerAttrValuesRequest();
      this.calculationGateway();
    }
  }

  /**
   * TODO: describe
   */
  get asOfError(): string | undefined {
    return this.$store.state.profiles.asOfError;
  }

/****************************************************************************************
 *      DATA SOURCES
 *      Sources are selected with checkboxes on this page (or loaded from a profile).
 *      Changes triggered from this component update the store.
 *      A watcher on the store is where subsequent actions are then triggered from.
 ****************************************************************************************/

  public async toggleSelectedDataSourceItem(val: string) {
    if (this.selectedDataSources.indexOf(val) > -1) {
      const arr = this.selectedDataSources.slice();
      arr.splice(this.selectedDataSources.indexOf(val), 1);
      this.$store.commit('setSelectedDataSources', arr);
    } else {
      this.$store.commit('setSelectedDataSources', this.selectedDataSources.concat(val));
    }
  }

  public addAllDataSources() {
    if (this.selectedDataSources.length < this.dataSources.length) {
      this.$store.commit('setSelectedDataSources', this.dataSources.map((obj: any) => obj.id));
    } else {
      this.$store.commit('setSelectedDataSources', []);
    }
  }

  get selectedDataSources(): string[] {
    return this.$store.state.selections.selectedDataSources || [];
  }

  @Watch('selectedDataSources')
  private onSelectedDataSourcesChange(value: string, oldValue: string) {
    if (!this.isLoadingFromProfile) {
      const selectedAttributes = this.selectedAttributes.slice();
      this.generateAvailableAttributes();
      this.triggerAttrValuesRequest();
      // If selected attributes have changed based on generateAvailableAttributes, then
      // we don't need to trigger a calculation as our attr watcher will do that
      if (!UtilsService.compareArrays(this.selectedAttributes, selectedAttributes)) {
        this.calculationGateway();
      }
    }   
  }


/****************************************************************************************
 *      ATTRIBUTES
 *      Attributes are selected with checkboxes on this page (or loaded from a profile).
 *      Changes triggered from this component update the store.
 *      A watcher on the store is where subsequent actions are then triggered from.  
 ****************************************************************************************/

  public async selectAttributesItem(val: string) {
    if (this.selectedAttributes.indexOf(val) > -1) {
      const arr = this.selectedAttributes.slice();
      arr.splice(this.selectedAttributes.indexOf(val), 1);
      this.$store.commit('setSelectedAttributes', arr);
    } else {
      this.$store.commit('setSelectedAttributes', this.selectedAttributes.concat(val));
    }
  }

  public addAllAttributes() {
    if (this.selectedAttributes.length < this.attributes.length) {
      this.$store.commit('setSelectedAttributes', this.attributes.map((obj: any) => obj.id));
    } else {
      this.$store.commit('setSelectedAttributes', []);
    }
  }

  /**
   * When we update our list of available attribute options, we want to ensure
   * that any previously selected attributes are still in that list. This removes
   * any selected attributes that are no longer available.
   */
  public updateSelectedAttributesAfterAttributeListHasBeenEdited() {
    const original = this.selectedAttributes.slice();
    const selectedAttributes = this.selectedAttributes.filter((attrId: string) => {
      return this.attributes.find(attr => attr.id === attrId);
    });
    if (UtilsService.compareArrays(selectedAttributes, original)) {
      this.$store.commit('setSelectedAttributes', selectedAttributes);
    }
  }

  get selectedAttributes(): string[] {
    return this.$store.state.selections.selectedAttributes || [];
  }

  @Watch('selectedAttributes')
  private onSelectedAttributesChange(value: string, oldValue: string) {
    if (!this.isLoadingFromProfile) {
      this.calculationGateway();
    }
  }

/****************************************************************************************
 *      BUCKETING
 *      Bucketing is set in a modal (SelectBucketingComponent), through the loading of
 *      a profile (TODO: check), or by falling back to the default as defined in the 
 *      api response from /options.
 *      We respond to changes with a *deep* watcher (onSelectedBucketingChange).
 ****************************************************************************************/

  public selectBucketing(attributeId: string): void {
    this.$store.commit('setAttributeIdUnderEdit', attributeId);
    this.$store.commit('setModal', 'selectBucketing');
  }

  public returnSelectedBucketingString(attributeId: string): string {
    const bucket: Bucket = this.$store.getters.getBucketingForAttribute(attributeId);

    if (bucket.type === 'regulatory') {
      return bucket.id + ' (Regulatory bucketing approach)';
    } else {
      return bucket.name + ' (custom bucket)'
    }
  }

  get selectedBucketing(): BucketMap {
    return this.$store.state.buckets.selectedBucketing;
  }

  @Watch('selectedBucketing', { deep: true })
  private onSelectedBucketingChange(value: string, oldValue: string) {
    if (!this.isLoadingFromProfile) {
      this.triggerAttrValuesRequest();
      this.calculationGateway();
    }
  }


/******************************
 *
 *    CALCULATION TRIGGERS
 *
 *******************************/

  public calculationGateway() {
    if (this.selectedProducts.length && this.selectedDataSources.length && this.selectedAttributes.length) {
      this.calculate();
    } else {
      this.$store.commit('setCurrentProfileShowingOnGraph'); // TODO: check
      this.$store.commit('setSelectedGraphIndex', 0); // TODO: remove after  merge
      this.$store.commit('setResult');
    }
  }

  public async calculate() {
    this.calculateLoading = true;

    let profile: ProfileForRequest = {
      sources: this.selectedDataSources,
      types: this.selectedProducts,
      attributes: this.selectedAttributes,
      asOf: this.asOf
    };

    // Filter selected attributes by those available for
    // the selected product & data sources
    profile.attributes = profile.attributes.filter((attrId: string) => {
      return this.attributes.find( (attr: any) => {
        return attr.id === attrId;
      });
    });

    // If we have bucketing, and a bucketable attr, create attribute_params for request
    profile = UtilsService.convertBucketToAttributeParams(profile, this.optionsData);

    //this.$store.commit('setSelectedGraphIndex', 0); // TODO: check in light of Rob merge
    GAService.sendEvent(this.$gtag, 'Calculate', 'Selection', '');
    await this.$store.dispatch('calculateResults', { profile });
    this.calculateLoading = false;
  }



/********************************************
 *
 *   Adjusting displayed data sources
 *
 *********************************************/

  


/********************************************
 *
 *   Adjusting displayed attributes
 *
 *********************************************/

  

/********************************************
 *
 *   Component lifecycle methods
 *
 *********************************************/

  public async mounted() {
    try {
      this.$store.dispatch('getOptions');
    } catch (e) {
      this.$store.commit('setToastError', 'Error retrieving options. Please try signing out and in.');
    }

    this.$store.commit('setResult');
    this.$store.commit('setCurrentProfileShowingOnGraph');
  }
}
</script>
<style scoped lang="scss">
@import '@/scss/partials/_settings.colors.scss';

  .profile-editor {
    width: 100%;
    display: flex;
    flex-direction: column;
  }

  .actions-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    max-width: 100%;
    overflow: hidden;

    .labs-loader {
      height: 28px;
      margin-left: 10px;
      &::after {
        width: 28px;
        height: 28px;
      }
    }

    .select-container {
      display: flex;
      align-items: center;
      font-weight: 600;
      overflow: hidden;
      justify-content: flex-start;

      h5 {
        font-size: 14px;
      }

      .select {
        margin-left: 5px;
        width: 100%;

        select {
          width: calc(100% - 5px);
        }
      }
    }

    .button {
      height: 36px;

      i {
        font-size: 20px;
        margin-left: 4px;
      }
    }

    .button-icon, .button-add-more {
      margin: {
        left: 5px;
        right: 5px;
      }

      i {
        margin-left: 0;
      }
    }

    .date-display {
      font-size: 12px;
      margin-left: 5px;
      text-align: right;

      .date {
        font-weight: normal;
      }
    }
  }

  .multi-products-row {
    font-size: 12px;
    width: 100%;
    display: flex;
    margin-top: 5px;

    .multi-products-list {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      text-align: left;
    }
    .pseudo-link {
      padding-left: 5px;
    }
  }

  .create-profile-row {
    width: 100%;
    display: flex;
    margin-top: 15px;
    flex-grow: 1;

    .select-column {
      padding-left: 10px;
      padding-right: 10px;
      display: flex;
      flex-direction: column;
      flex: 1;
      width: 50%;

      &:first-child {
        padding-left: 0;
      }

      &:last-child {
        padding-right: 0;
      }

      h5 {
        width: 100%;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        font-size: 14px;
      }

      .select-container {
        height: 200px;
        background-color: #fff;
        border: 1px solid $border-color;
        margin-top: 7px;
        flex-grow: 1;
        display: flex;
        position: relative;

        .select-all-items {
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-bottom: 1px solid $border-color;
          padding: 8px 10px;
          font-size: 14px;
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          background-color: #fff;
          z-index: 1;

          .button {
            font-size: 13px;
            padding-top: 5px;
            padding-bottom: 5px;

            span {
              font-size: 13px;
            }
          }
        }

        .select-header {
          position: absolute;
          top: 46px;
          left: 0;
          right: 0;
          background-color: #f2f3f7;
          z-index: 1;
          border-bottom: 1px solid $border-color;
          font-size: 12px;
          text-align: right;
          padding: 2px 10px;
          font-weight: 500;
          color: $grey-120;

          .info-icon {
            i {
              font-size: 16px;
              position: relative;
              top: 4px;
              color: $grey-120;
            }

            .info {
              display: none;
            }

            &:hover {
              cursor: default;

              .info-outline {
                display: none;
              }

              .info {
                display: inline-block;
              }
            }
          }
        }

        .select-scrollable {
          overflow-y: scroll;
          flex-grow: 1;

          .select-inner {
            padding: 0;
            padding-top: 69px;
            min-height: 100%;
            display: flex;
            flex-direction: column;

            &--empty {
              justify-content: center;
            }

            .select-all-empty {
              padding: 0 20px;

              p {
                text-align: center;
                margin-top: 20px;
              }

              .icon-layout-3 {
                font-size: 48px;
                color: $grey-200;
              }
            }

            .select-item {
              position: relative;
              padding: 10px;
              padding-left: 40px;
              border-bottom: 1px solid $border-color;
              text-align: left;
              max-width: 100%;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;

              &--with-count, &--with-flex {
                display: flex;
                justify-content: space-between;
                white-space: normal;
                text-overflow: clip;
              }

              .select-item__left {
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                padding-right: 3px;

                .select-item__bucketing {
                  //border: 1px solid red;
                  white-space: nowrap;
                  overflow: hidden;
                  text-overflow: ellipsis;
                  padding-right: 3px;
                  font-size: 12px;
                  margin-top: -3px;

                  i {
                    font-size: 14px;
                    position: relative;
                    top: 3px;
                    color: $grey-180;
                  }

                  &:hover {
                    color: $refinitiv-blue;
                    text-decoration: underline;

                    i {
                      color: $refinitiv-blue;
                    }
                  }
                }
              }

              .select-item__count--dev {
                color: orange;
              }

              .select-item__checkbox {
                position: absolute;
                width: 14px;
                height: 14px;
                background-color: #fff;
                border: 2px solid $border-color;
                top: 14px;
                left: 10px;
              }

              .select-item__radio {
                position: absolute;
                width: 16px;
                height: 16px;
                background-color: #fff;
                border: 2px solid $border-color;
                top: 14px;
                left: 10px;
                border-radius: 8px;
              }

              &--active {
                .select-item__radio {
                  border-color: $refinitiv-blue;

                  &::before {
                    content: '';
                    position: absolute;
                    width: 8px;
                    height: 8px;
                    background-color: $refinitiv-blue;
                    top: 2px;
                    left: 2px;
                    border-radius: 50%;
                  }
                }

                .select-item__checkbox {
                  border-color: $refinitiv-blue;

                  &::before {
                    content: '';
                    position: absolute;
                    width: 8px;
                    height: 8px;
                    background-color: $refinitiv-blue;
                    top: 1px;
                    left: 1px;
                  }
                }
              }

              &:hover {
                cursor: pointer;
                background-color: #f0f0f0;
              }
            }
          }
        }


      }
    }

  }

  .calculate-row {
    margin-top: 25px;
    display: flex;
    justify-content: flex-end;

    .button-secondary {
      margin-right: 10px;
    }
  }
</style>
